import {
  createFileRoute,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import type { GetMyCampaignStatusesResponse } from "schema/gen/es/chiikipoint/wallet/v2/service_pb";
import { css } from "../../../styled-system/css";
import Header from "../../components/header";
import {
  getCurrentOsakiChargeBonusCampaignPeriod,
  getCurrentOsakiChargeBonusCampaignStatus,
  getOsakiChargeBonusLotteryResultAnnouncementDateText,
} from "../../features/charge/osaki-charge-bonus-campaign";
import type { WalletClient } from "../../libs/client";
import { getCurrentTime } from "../../libs/date";
import { t } from "../../libs/i18n";
import { DefaultCampaignDetail } from "./about-campaign/-components/default-campaign-detail";
import { KesennumaCampaignDetail } from "./about-campaign/-components/kesennuma-campaign-detail";
import { MarumoriCampaignDetail } from "./about-campaign/-components/marumori-campaign-detail";
import { MiyagiCampaignDetail } from "./about-campaign/-components/miyagi-campaign-detail";
import { OsakiCampaignDetail } from "./about-campaign/-components/osaki-campaign-detail";
import { OsakiChargeBonusCampaignDetail } from "./about-campaign/-components/osaki-charge-bonus-campaign-detail";
import { SendaiCampaignDetail } from "./about-campaign/-components/sendai-campaign-detail";
import { ShigaOtsuCampaignDetail } from "./about-campaign/-components/shiga-otsu-campaign-detail";
import { ShiroishiCampaignDetail } from "./about-campaign/-components/shiroishi-campaign-detail";
import { TagajoCampaignDetail } from "./about-campaign/-components/tagajo-campaign-detail";
import { TomeCampaignDetail } from "./about-campaign/-components/tome-campaign-detail";

type AboutCampaignSearch = {
  type?:
    | "osaki"
    | "osaki-charge-bonus"
    | "miyagi"
    | "sendai"
    | "shiroishi"
    | "tagajo"
    | "kesennuma"
    | "tome";
};

export const Route = createFileRoute("/_authed/about-campaign")({
  validateSearch: (search: Record<string, unknown>): AboutCampaignSearch => {
    return {
      type: search.type as
        | "osaki"
        | "osaki-charge-bonus"
        | "miyagi"
        | "sendai"
        | "shiroishi"
        | "tagajo"
        | "kesennuma"
        | "tome"
        | undefined,
    };
  },
  loader: async ({ context: { client, tenant } }) => {
    const [campaignStatuses, currentTime] =
      tenant.slug === "miyagi"
        ? await Promise.all([
            client.getMyCampaignStatuses({}),
            getCurrentTime(),
          ])
        : [null, null];
    return {
      tenant,
      campaignStatuses,
      currentTime,
    };
  },
  component: AboutCampaign,
});

function getCampaignDetailComponent(
  tenantSlug: string,
  client: WalletClient,
  campaignStatuses: GetMyCampaignStatusesResponse | null,
  currentDate: Date | null,
  onApplied: () => Promise<void>,
  type:
    | "osaki"
    | "osaki-charge-bonus"
    | "miyagi"
    | "sendai"
    | "shiroishi"
    | "tagajo"
    | "kesennuma"
    | "tome"
    | undefined,
): React.ReactElement {
  if (tenantSlug === "miyagi") {
    if (type === "osaki-charge-bonus") {
      const currentPeriod = currentDate
        ? getCurrentOsakiChargeBonusCampaignPeriod(currentDate)
        : null;
      // 募集期間外は1期の内容を見せる
      const campaignTerm = currentPeriod?.term ?? "1st";
      return (
        <OsakiChargeBonusCampaignDetail
          client={client}
          status={
            currentDate
              ? getCurrentOsakiChargeBonusCampaignStatus(
                  campaignStatuses,
                  currentDate,
                )
              : null
          }
          canApply={currentPeriod?.phase === "application"}
          campaignTerm={campaignTerm}
          lotteryResultAnnouncementDateText={getOsakiChargeBonusLotteryResultAnnouncementDateText(
            campaignTerm,
          )}
          onApplied={onApplied}
        />
      );
    }
    if (type === "osaki") {
      return <OsakiCampaignDetail />;
    }
    if (type === "sendai") {
      return <SendaiCampaignDetail />;
    }
    if (type === "shiroishi") {
      return <ShiroishiCampaignDetail />;
    }
    if (type === "tagajo") {
      return <TagajoCampaignDetail />;
    }
    if (type === "kesennuma") {
      return <KesennumaCampaignDetail />;
    }
    if (type === "tome") {
      return <TomeCampaignDetail />;
    }
    return <MiyagiCampaignDetail />;
  }
  if (tenantSlug === "marumori") {
    return <MarumoriCampaignDetail />;
  }
  if (tenantSlug === "shiga-otsu") {
    return <ShigaOtsuCampaignDetail />;
  }
  return <DefaultCampaignDetail />;
}

function AboutCampaign() {
  const navigate = useNavigate();
  const router = useRouter();
  const { client } = Route.useRouteContext();
  const { tenant, campaignStatuses, currentTime } = Route.useLoaderData();
  const { type } = Route.useSearch();

  return (
    <>
      <Header
        title={
          type === "miyagi"
            ? t("「みやぎ防災」ウェルカムポイント詳細")
            : t("キャンペーン詳細")
        }
        onClickBack={() => {
          navigate({ to: "/" });
        }}
      />

      <div
        className={css({
          p: "24px",
          pb: "64px",
        })}
      >
        {getCampaignDetailComponent(
          tenant.slug,
          client,
          campaignStatuses,
          currentTime?.date ?? null,
          () => router.invalidate(),
          type,
        )}
      </div>
    </>
  );
}
